import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Box, Typography, Paper, Tab, Tabs, Chip, Stack, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import ShoonyaPlaceAccount from './ShoonyaPlaceAccount';
import ZerodhaPlaceAccount from './ZerodhaPlaceAccount';
import axios from 'axios';

const StatusChip = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Chip
      label={value}
      size="small"
      sx={{
        bgcolor: value === 'Present' ? '#dcfce7' : '#fee2e2',
        color: value === 'Present' ? '#15803d' : '#b91c1c',
        fontWeight: 600, fontSize: '0.7rem', height: 20,
      }}
    />
  </Box>
);

const LogViewer = () => {
  const [logs, setLogs] = useState('');
  const [totalLines, setTotalLines] = useState(0);
  const [lineCount, setLineCount] = useState(300);
  const [loading, setLoading] = useState(false);
  const [logFiles, setLogFiles] = useState([]);
  const [showOlder, setShowOlder] = useState(false);
  const textareaRef = useRef(null);

  const fetchLogs = () => {
    setLoading(true);
    axios.get(`/api/logs?lines=${lineCount}`)
      .then(r => { setLogs(r.data.logs); setTotalLines(r.data.total_lines); })
      .catch(() => setLogs('Failed to fetch logs.'))
      .finally(() => setLoading(false));
  };

  const fetchFileList = () => {
    axios.get('/api/logs/files')
      .then(r => setLogFiles(r.data.files || []))
      .catch(() => setLogFiles([]));
  };

  useEffect(() => { fetchLogs(); }, [lineCount]); // eslint-disable-line

  useEffect(() => {
    if (textareaRef.current) textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
  }, [logs]);

  const handleShowOlder = () => {
    if (!showOlder) fetchFileList();
    setShowOlder(v => !v);
  };

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Show last</InputLabel>
          <Select value={lineCount} label="Show last" onChange={e => setLineCount(e.target.value)}>
            <MenuItem value={100}>100 lines</MenuItem>
            <MenuItem value={300}>300 lines</MenuItem>
            <MenuItem value={500}>500 lines</MenuItem>
            <MenuItem value={1000}>1000 lines</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" onClick={fetchLogs} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
        <Button variant="contained" size="small" onClick={() => window.open('/api/logs/download?file=app.log', '_blank')}>
          Download Today
        </Button>
        <Button variant="outlined" size="small" color="secondary" onClick={handleShowOlder}>
          {showOlder ? 'Hide Older Logs' : 'Retrieve Older Logs'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Last 7 days &bull; {totalLines} lines
        </Typography>
      </Box>

      {/* Older log files list */}
      {showOlder && (
        <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2">All Stored Log Files</Typography>
            <Button size="small" onClick={fetchFileList}>Refresh List</Button>
          </Box>
          {logFiles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No files found.</Typography>
          ) : (
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: '1px solid #e2e8f0' }}>
                  {['File', 'Size', 'Last Modified', ''].map(h => (
                    <Box component="th" key={h} sx={{ textAlign: 'left', py: 0.5, px: 1, color: 'text.secondary', fontWeight: 600 }}>{h}</Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {logFiles.map(f => (
                  <Box component="tr" key={f.name} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                    <Box component="td" sx={{ py: 0.5, px: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>{f.name}</Box>
                    <Box component="td" sx={{ py: 0.5, px: 1, color: 'text.secondary' }}>{f.size_kb} KB</Box>
                    <Box component="td" sx={{ py: 0.5, px: 1, color: 'text.secondary' }}>{f.modified}</Box>
                    <Box component="td" sx={{ py: 0.5, px: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => window.open(`/api/logs/download?file=${f.name}`, '_blank')}>
                        Download
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Log viewer */}
      <Box
        ref={textareaRef}
        component="pre"
        sx={{
          bgcolor: '#0f172a', color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.72rem',
          p: 2, borderRadius: 1, height: 480, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}
      >
        {logs || 'No logs yet.'}
      </Box>
    </Box>
  );
};

const Admin = ({ onSave }) => {
  const [tab, setTab] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [totpKey, setTotpKey] = useState('');
  const [message, setMessage] = useState('');
  const [dataStatus, setDataStatus] = useState({});

  useEffect(() => {
    axios.get('/api/get_master_account').then(r => {
      const d = r.data;
      setApiKey(d.api_key || ''); setApiSecret(d.api_secret || '');
      setUserId(d.user_id || ''); setPassword(d.password || ''); setTotpKey(d.totp_key || '');
    }).catch(() => setMessage('Failed to fetch master account.'));

    axios.get('/api/check_data_status').then(r => setDataStatus(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      await axios.post('/api/save_master_account', { api_key: apiKey, api_secret: apiSecret, user_id: userId, password, totp_key: totpKey });
      setMessage('Master account saved successfully!');
      onSave();
      setTimeout(() => window.location.reload(), 1000);
    } catch { setMessage('Error saving credentials.'); }
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Admin Settings</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage trading accounts and API credentials
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Main Panel */}
        <Paper sx={{ flex: 1, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: '1px solid #e2e8f0', px: 1, minHeight: 48, bgcolor: '#f8fafc' }}
          >
            <Tab label="Master Account" />
            <Tab label="Shoonya Accounts" />
            <Tab label="Zerodha Accounts" />
            <Tab label="Logs" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {tab === 0 && (
              <Box sx={{ maxWidth: 520 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Zerodha Master Credentials</Typography>
                <Stack spacing={1.5}>
                  <TextField label="API Key" value={apiKey} onChange={e => setApiKey(e.target.value)} fullWidth />
                  <TextField label="API Secret" value={apiSecret} onChange={e => setApiSecret(e.target.value)} fullWidth type="password" />
                  <TextField label="User ID" value={userId} onChange={e => setUserId(e.target.value)} fullWidth />
                  <TextField label="Password" value={password} onChange={e => setPassword(e.target.value)} fullWidth type="password" />
                  <TextField label="TOTP Key" value={totpKey} onChange={e => setTotpKey(e.target.value)} fullWidth />
                </Stack>
                <Button variant="contained" onClick={handleSave} sx={{ mt: 3, px: 3 }}>
                  Save Master Account
                </Button>
                {message && (
                  <Typography variant="body2" sx={{ mt: 2, color: message.includes('Error') ? 'error.main' : 'success.main' }}>
                    {message}
                  </Typography>
                )}
              </Box>
            )}
            {tab === 1 && <ShoonyaPlaceAccount />}
            {tab === 2 && <ZerodhaPlaceAccount />}
            {tab === 3 && <LogViewer />}
          </Box>
        </Paper>

        {/* DB Status Panel */}
        <Paper sx={{ width: 240, borderRadius: 2, flexShrink: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle2">Database Status</Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            {dataStatus.error ? (
              <Typography variant="body2" color="error">{dataStatus.error}</Typography>
            ) : (
              <>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Master
                </Typography>
                <StatusChip label="Master Account" value={dataStatus.master_account || 'Unknown'} />

                {dataStatus.order_accounts && (
                  <>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Shoonya
                    </Typography>
                    {Object.entries(dataStatus.order_accounts).map(([k, v]) => (
                      <StatusChip key={k} label={k} value={v} />
                    ))}
                  </>
                )}

                {dataStatus.zerodha_accounts && (
                  <>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, mb: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Zerodha
                    </Typography>
                    {Object.entries(dataStatus.zerodha_accounts).map(([k, v]) => (
                      <StatusChip key={k} label={k} value={v} />
                    ))}
                  </>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Admin;
